import React, { useState } from "react";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT_OPTIONS } from "@/lib/format";
import { toast } from "sonner";
import { Check } from "lucide-react";

// Quick inquiry tied to a specific watch OR part. depositIntent changes copy.
// Backward compatible: passing `watch` behaves exactly as before. Passing `part`
// submits the shared watch-request with item_type="part".
export default function InquiryDialog({ watch, part, label, depositIntent = false, variant }) {
  const isPart = !!part && !watch;
  const item = watch || part || null;
  const resolvedLabel = label || (isPart ? "Inquire / Request" : "Request This Watch");
  const titleText = isPart ? "Request This Part" : "Request This Watch";
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", preferred_contact: "email", message: "", consent_contact: true, vip_signup: true,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const partSummary = isPart
    ? `Interested in part: ${part.title}${part.part_number ? ` (Part #${part.part_number})` : ""}${part.reference_number ? ` for ref ${part.reference_number}` : ""}.`
    : (depositIntent ? "Interested in this listing. Deposit checkout is not live yet - Stage 7 placeholder." : "Interested in this listing.");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await publicApi.post("/public/watch-request", {
        full_name: form.full_name, email: form.email, phone: form.phone,
        preferred_contact: form.preferred_contact, item_type: isPart ? "part" : "watch",
        brand_wanted: item?.brand, model_wanted: item?.model, reference_number: item?.reference_number,
        deposit_willingness: depositIntent ? "Interested when deposit checkout is available (not live yet)" : null,
        special_requests: form.message ? `${form.message}${isPart ? `\n\n${partSummary}` : ""}` : partSummary,
        vip_signup: form.vip_signup, consent_contact: form.consent_contact,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(false); }}>
      <DialogTrigger asChild>
        <Button data-testid={depositIntent ? "deposit-inquiry-btn" : (isPart ? "request-part-btn" : "request-watch-btn")}
          className={variant === "outline"
            ? "border border-[#c9a86a] bg-transparent text-[#c9a86a] hover:bg-[#c9a86a] hover:text-[var(--on-gold)] rounded-none"
            : "bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none"}>
          {resolvedLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--panel-2)] border-[var(--border-2)] text-[var(--text)]">
        {done ? (
          <div className="text-center py-6" data-testid="inquiry-success">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full border border-[#c9a86a] text-[#c9a86a] mb-4"><Check size={26} /></div>
            <h3 className="text-xl font-semibold text-[var(--text)]">Request received</h3>
            <p className="text-[var(--text-2)] mt-2 text-sm">A member of the house will be in touch shortly{isPart ? ` about the ${part.title}` : (watch ? ` about the ${watch.brand} ${watch.model}` : "")}.</p>
            <Button onClick={() => setOpen(false)} className="mt-6 bg-[#c9a86a] text-[var(--on-gold)] rounded-none">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="text-[var(--text)]">{titleText}</DialogTitle></DialogHeader>
            {isPart
              ? <p className="text-sm text-[var(--text-2)] -mt-2">{part.title}{part.reference_number ? ` · ref ${part.reference_number}` : ""}{part.part_number ? ` · #${part.part_number}` : ""}</p>
              : (watch && <p className="text-sm text-[var(--text-2)] -mt-2">{watch.brand} {watch.model}{watch.reference_number ? ` · ${watch.reference_number}` : ""}</p>)}
            {depositIntent && <p className="text-xs text-amber-400/90">Deposit payments are not live yet (Stage 7 placeholder). This inquiry does not reserve the watch.</p>}
            <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-[var(--text-2)] text-sm">Full Name *</Label><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="inq-name" /></div>
                <div className="space-y-1.5"><Label className="text-[var(--text-2)] text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="inq-email" /></div>
                <div className="space-y-1.5"><Label className="text-[var(--text-2)] text-sm">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" /></div>
                <div className="space-y-1.5"><Label className="text-[var(--text-2)] text-sm">Preferred Contact</Label>
                  <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
                    <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTACT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-[var(--text-2)] text-sm">Message</Label><Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="inq-message" /></div>
              <label className="flex items-center gap-3 cursor-pointer"><Checkbox checked={form.vip_signup} onCheckedChange={(v) => set("vip_signup", !!v)} /><span className="text-sm text-[var(--text-2)]">Also add me to the VIP list</span></label>
              <Button type="submit" disabled={busy} data-testid="inq-submit" className="w-full bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none py-5">{busy ? "Sending…" : "Send Request"}</Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
